import React from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  focusBillingDialogElement,
  handleBillingDialogKeyDown,
} from "../../utils/billingDialogAccessibility.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import {
  diagnosticCount,
  filterPullForwardDiagnosticsOrders,
  groupPullForwardDiagnosticsOrders,
  normalizePullForwardDiagnostics,
  PULL_FORWARD_DIAGNOSTIC_FILTERS,
} from "./pullForwardDiagnostics.js";

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function formatNumber(value, maximumFractionDigits = 1) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "—";
  }
  return new Intl.NumberFormat("fr-CA", { maximumFractionDigits }).format(number);
}

function formatDate(value) {
  const normalizedValue = normalizeText(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return normalizedValue || "Date inconnue";
  }
  const [year, month, day] = normalizedValue.split("-").map(Number);
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" })
    .format(new Date(year, month - 1, day));
}

const READY_REASON_CODES = new Set([
  "ready",
  "compatible",
  "compatible_same_site",
  "candidate_ready",
  "target_ready",
]);

const CAPACITY_REASON_CODES = new Set([
  "capacity_insufficient",
  "capacity_to_free",
  "compartment_capacity_insufficient",
  "current_capacity_insufficient",
]);

const MANUAL_SPLIT_REASON_CODES = new Set(["client_site_split_required"]);

const ERROR_REASON_CODES = new Set([
	"analysis_unavailable",
  "analysis_error",
  "analysis_pending",
  "candidate_runtime_unavailable",
  "candidate_workbook_unavailable",
  "effective_payload_mismatch",
  "preview_replay_unavailable",
  "runtime_revision_unstable",
]);

const PENDING_REASON_CODES = new Set(["analysis_pending"]);

const CONSTRAINT_REASON_LABELS = {
  aspirator_required_no_compatible_truck: "Aspirateur requis",
  truck_disabled: "Camion désactivé",
  dispatch_disabled: "Camion désactivé",
  truck_protected: "Camion protégé",
  target_truck_protected: "Camion protégé",
  published_trip: "Trajet publié",
  target_trip_published: "Trajet publié",
  target_missing: "Cible introuvable",
  target_not_last_trip: "Trajet cible non final",
  target_execution_or_freeze: "Exécution ou gel opérationnel",
  truck_dispatch_disabled: "Camion désactivé",
  execution_started: "Exécution déjà commencée",
  target_execution_started: "Exécution déjà commencée",
  locked_truck_mismatch: "Camion verrouillé différent",
  truck_type_incompatible: "Type de camion incompatible",
  truck_type_unknown: "Type de camion inconnu",
  schedule_constraints: "Contraintes d’horaire",
  future_schedule_constraints: "Contraintes d’horaire futures",
  existing_schedule_constraints: "Contraintes d’horaire existantes",
  work_duration_unavailable: "Durée de travail indisponible",
  work_limit_exceeded: "Limite de travail dépassée",
  grain_type_unavailable: "Produit futur indisponible",
  grain_type_mismatch: "Produit incompatible",
  ontario_incompatible: "Contrainte Ontario",
  aspirator_missing: "Aspirateur requis",
  preloaded_previous_day_unsupported: "Chargement de la veille non pris en charge",
  compartment_plan_unavailable: "Répartition des compartiments indisponible",
  initial_load_unavailable: "Charge initiale indisponible",
  capacity_unavailable: "Capacité indisponible",
  current_group_quantity_unavailable: "Quantité du groupe indisponible",
  group_capacity_exceeded: "Capacité du groupe dépassée",
  client_site_split_required: "Séparation manuelle requise",
  client_site_group_ambiguous: "Quantité du groupe non prouvable",
  j1_target_dependency: "Dépendance au bloc J+1",
  product_incompatible: "Produit incompatible",
  time_insufficient: "Temps de travail insuffisant",
  max_work_exceeded: "Limite de travail dépassée",
  citerne_exact_client_site_unavailable: "Client/site absent du dispatch",
  loading_visit_missing: "Chargement correspondant absent",
  loading_site_mismatch: "Lieu de chargement différent",
  loading_contract_mismatch: "Chargement incompatible",
  loading_visit_ambiguous: "Plusieurs chargements correspondants",
  delivery_visit_missing: "Livraison correspondante absente",
  delivery_visit_ambiguous: "Plusieurs livraisons correspondantes",
  physical_topology_changed: "Ordre du trajet incompatible",
};

function statusRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : { status: value };
}

function normalizedBusinessStatus(value) {
  return normalizeText(statusRecord(value).status).toLowerCase();
}

function filterVisibleCandidateBlocks(value) {
  const blocks = Array.isArray(value) ? value : [];
  return blocks.filter((block) => (
    !["ineligible", "needs_capacity"].includes(normalizedBusinessStatus(block))
    && resolveRequiredTruckType(block) === TRUCK_TYPE_CITERNE
  ));
}

function normalizedReasonCode(value) {
  return normalizeText(value?.reason_code || value?.reasonCode).toLowerCase();
}

function normalizedStatusLabel(value) {
  return normalizeText(value?.status_label || value?.statusLabel).toLowerCase();
}

function withoutAccents(value) {
  return normalizeText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function reasonCodeLabel(reasonCode) {
  if (!reasonCode) {
    return "";
  }
  if (READY_REASON_CODES.has(reasonCode)) {
    return "Prêt";
  }
  if (CAPACITY_REASON_CODES.has(reasonCode)) {
    return "Capacité insuffisante";
  }
  if (CONSTRAINT_REASON_LABELS[reasonCode]) {
    return CONSTRAINT_REASON_LABELS[reasonCode];
  }
  return "";
}

function businessStatusDescriptor(value) {
  const record = statusRecord(value);
  const status = normalizedBusinessStatus(record);
  const statusLabelValue = normalizeText(record.status_label || record.statusLabel);
  const normalizedLabel = withoutAccents(statusLabelValue);
  const reasonCode = normalizedReasonCode(record);
  const reasonLabelValue = normalizeText(record.reason_label || record.reasonLabel);
  const isError = status === "error"
	|| status === "analysis_unavailable"
    || normalizedLabel === "erreur"
    || ERROR_REASON_CODES.has(reasonCode);
  if (isError && !PENDING_REASON_CODES.has(reasonCode)) {
    return {
      className: "border-red-200 bg-red-50 text-red-800",
      kind: "error",
      label: "",
      status,
    };
  }
  if (normalizedLabel === "analyse" || PENDING_REASON_CODES.has(reasonCode)) {
    return {
      className: "border-slate-200 bg-slate-100 text-slate-700",
      kind: "pending",
      label: "",
      status,
    };
  }
  const explicitReady = READY_REASON_CODES.has(reasonCode)
    || ["pret", "pret a previsualiser", "ready"].includes(normalizedLabel);
  const explicitCapacity = CAPACITY_REASON_CODES.has(reasonCode)
    || normalizedLabel === "capacite"
    || normalizedLabel.includes("capacite insuffisante");
  const explicitManualSplit = MANUAL_SPLIT_REASON_CODES.has(reasonCode)
    || status === "manual_split_required"
    || normalizedLabel.includes("separation manuelle requise");

  if (explicitReady || (!statusLabelValue && !reasonCode && status === "ready")) {
    return {
      className: "border-emerald-200 bg-emerald-50 text-emerald-800",
      kind: "ready",
      label: "Prêt",
      status,
    };
  }
  if (explicitManualSplit) {
    return {
      className: "border-violet-200 bg-violet-50 text-violet-800",
      kind: "manual_split",
      label: "Séparation manuelle requise",
      status,
    };
  }
  if (explicitCapacity || (!statusLabelValue && !reasonCode && status === "needs_capacity")) {
    return {
      className: "border-amber-200 bg-amber-50 text-amber-800",
      kind: "capacity",
      label: "Capacité insuffisante",
      status,
    };
  }
  const codeLabel = reasonCodeLabel(reasonCode);
  const genericStatusLabel = [
    "contrainte identifiee",
    "constraint identified",
    "inelegible",
    "ineligible",
    "non admissible",
  ].includes(normalizedLabel);
  if (genericStatusLabel && (codeLabel || reasonLabelValue)) {
    return {
      className: "border-amber-200 bg-amber-50 text-amber-800",
      kind: "constraint",
      label: codeLabel || reasonLabelValue,
      status,
    };
  }
  if (statusLabelValue) {
    return {
		className: "border-amber-200 bg-amber-50 text-amber-800",
      kind: "constraint",
      label: statusLabelValue,
      status,
    };
  }
  if (codeLabel && !READY_REASON_CODES.has(reasonCode) && !CAPACITY_REASON_CODES.has(reasonCode)) {
    return {
      className: "border-amber-200 bg-amber-50 text-amber-800",
      kind: "constraint",
      label: codeLabel,
      status,
    };
  }
  switch (status) {
    case "ineligible":
      return {
        className: "border-amber-200 bg-amber-50 text-amber-800",
        kind: "constraint",
		label: codeLabel || reasonLabelValue || "À vérifier",
        status,
      };
    default:
      return {
        className: "border-slate-200 bg-slate-100 text-slate-700",
        kind: "unknown",
        label: "À vérifier",
        status,
      };
  }
}

function statusLabel(value) {
  return businessStatusDescriptor(value).label;
}

function statusClassName(value) {
  return businessStatusDescriptor(value).className;
}

function compactStatusLabel(value) {
  return businessStatusDescriptor(value).label;
}

function progressStateLabel(progressState) {
  switch (normalizeText(progressState).toLowerCase()) {
    case "running":
      return "Analyse en cours";
    case "error":
      return "Erreur";
    case "pending":
    default:
      return "En attente";
  }
}

function progressStateClassName(progressState) {
  switch (normalizeText(progressState).toLowerCase()) {
    case "running":
      return "border-sky-300 bg-sky-100 text-sky-800";
    case "error":
      return "border-red-200 bg-red-50 text-red-800";
    case "pending":
    default:
      return "border-slate-200 bg-slate-100 text-slate-600";
  }
}

function isReadyBusinessStatus(value) {
  return businessStatusDescriptor(value).kind === "ready";
}

function isReadyTarget(target) {
  // Keep the legacy status check explicit while allowing the new label/code
  // fields to take precedence when they are available.
  const legacyReady = normalizeText(target?.status).toLowerCase() === "ready";
  const statusLabelValue = withoutAccents(normalizeText(target?.status_label || target?.statusLabel));
  const reasonCode = normalizedReasonCode(target);
  if (statusLabelValue || reasonCode) {
    return isReadyBusinessStatus(target);
  }
  return legacyReady;
}

function blockProgressState(block) {
  const explicitState = normalizeText(block?.progress_state).toLowerCase();
	if (["compatible", "incompatible"].includes(explicitState)) {
	  return "completed";
	}
  if (["pending", "running", "completed", "compatible", "incompatible", "error"].includes(explicitState)) {
    return explicitState;
  }
	return normalizeText(block?.status).toLowerCase() === "ready" ? "completed" : "pending";
}

const CURRENT_DISPATCH_UPDATE_REASON = "le dispatch courant doit être mis à jour avant le regroupement";

function requiresCurrentDispatchUpdate(reason) {
  return normalizeText(reason)
    .toLowerCase()
    .replace(/[.!?]+$/u, "") === CURRENT_DISPATCH_UPDATE_REASON;
}

function dispatcherStatusLabel(value, reason) {
	return compactStatusLabel(value);
}

function dispatcherReasonLabel(reason) {
  return requiresCurrentDispatchUpdate(reason)
    ? "Le dispatch a changé depuis sa dernière optimisation."
    : normalizeText(reason);
}

function targetKey(target) {
  return `${normalizeText(target?.target_truck_id)}::${normalizeText(target?.target_trip_id)}`;
}

function targetLabel(target, fallbackLabel = "Citerne") {
  return normalizeText(target?.truck_name) || normalizeText(target?.target_truck_id) || fallbackLabel;
}

const TRUCK_TYPE_CITERNE = "citerne";

const PULL_FORWARD_TRUCK_TYPE_LABELS = {
  [TRUCK_TYPE_CITERNE]: {
    title: "Citerne",
    singular: "Citerne proposée",
    plural: "Citernes proposées",
  },
};

const PULL_FORWARD_SECTIONS = [
  {
    type: TRUCK_TYPE_CITERNE,
    title: "Citerne",
    description: "Chaque ligne regroupe les commandes compatibles par date, chargement, client/site et produit normalisé; les blocs incompatibles sont masqués.",
    emptyLabel: "Aucun bloc Citerne correspondant n’est disponible.",
  },
];

function buildCandidateSections(blocks) {
  const visibleBlocks = filterVisibleCandidateBlocks(blocks);
  return PULL_FORWARD_SECTIONS.map((section) => ({
    ...section,
    blocks: visibleBlocks
      .filter((block) => resolveRequiredTruckType(block) === section.type)
      .sort(compareCandidateBlocks),
  }));
}

function normalizeTruckType(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized.includes(TRUCK_TYPE_CITERNE)) {
    return TRUCK_TYPE_CITERNE;
  }
  return normalized ? "unsupported" : "";
}

function resolveRequiredTruckType(block) {
  const explicitType = normalizeTruckType(block?.required_truck_type);
  if (explicitType) {
    return explicitType;
  }
  // Older payloads omitted the type; Pull Forward now accepts only Citerne.
  return TRUCK_TYPE_CITERNE;
}

function targetOptionsForBlock(block, truckType = resolveRequiredTruckType(block)) {
  const options = (Array.isArray(block?.target_options) ? block.target_options : []).filter(Boolean);
  return truckType === TRUCK_TYPE_CITERNE ? options : [];
}

function compareCandidateBlocks(firstBlock, secondBlock) {
  const firstDayOffset = Number(firstBlock?.day_offset);
  const secondDayOffset = Number(secondBlock?.day_offset);
  const firstDay = Number.isFinite(firstDayOffset) ? firstDayOffset : Number.POSITIVE_INFINITY;
  const secondDay = Number.isFinite(secondDayOffset) ? secondDayOffset : Number.POSITIVE_INFINITY;
  if (firstDay !== secondDay) {
    return firstDay - secondDay;
  }

  const clientComparison = normalizeText(firstBlock?.client_name)
    .localeCompare(normalizeText(secondBlock?.client_name), "fr", { sensitivity: "base" });
  if (clientComparison !== 0) {
    return clientComparison;
  }

  const firstOrderKey = Array.isArray(firstBlock?.order_ids)
    ? firstBlock.order_ids.map(normalizeText).filter(Boolean).join("\u0000")
    : "";
  const secondOrderKey = Array.isArray(secondBlock?.order_ids)
    ? secondBlock.order_ids.map(normalizeText).filter(Boolean).join("\u0000")
    : "";
  const orderComparison = firstOrderKey.localeCompare(secondOrderKey, "fr", { sensitivity: "base" });
  if (orderComparison !== 0) {
    return orderComparison;
  }
  return normalizeText(firstBlock?.block_id).localeCompare(normalizeText(secondBlock?.block_id), "fr", { sensitivity: "base" });
}

const METRIC_LABELS = {
  added_quantity_tons: "Tonnage ajouté",
  baseline_loading_visit_count: "Visites de chargement avant",
  capacity_remaining_tons: "Capacité restante",
  capacity_tons: "Capacité du camion",
  capacity_to_free_tons: "Capacité à libérer",
  current_loading_visit_quantity_tons: "Tonnage de la visite avant",
  current_group_quantity_tons: "Groupe client/site avant",
  distance_delta_km: "Distance ajoutée",
  duration_delta_minutes: "Durée ajoutée",
  effective_max_work_minutes: "Limite de travail effective",
  loading_visit_delta: "Visites de chargement ajoutées",
  overtime_authorized: "Dépassement autorisé",
  overtime_minutes: "Dépassement",
  projected_loading_visit_count: "Visites de chargement après",
  projected_loading_visit_quantity_tons: "Tonnage de la visite après",
  projected_group_quantity_tons: "Groupe client/site après",
  other_group_quantity_tons: "Autres groupes de la visite",
  projected_peak_load_tons: "Charge maximale projetée",
  projected_work_minutes: "Durée projetée",
  remaining_capacity_tons: "Capacité restante",
  route_source: "Source du trajet",
};

// Keep internal replay and diagnostic metadata out of the dispatcher-facing
// validation summary. The backend may add more fields without exposing them
// accidentally through the generic object renderer.
const PUBLIC_METRIC_KEYS = new Set([
  "added_quantity_tons",
  "baseline_loading_visit_count",
  "capacity_tons",
  "capacity_remaining_tons",
  "capacity_to_free_tons",
  "current_loading_visit_quantity_tons",
  "current_group_quantity_tons",
  "remaining_capacity_tons",
  "loading_visit_delta",
  "projected_peak_load_tons",
  "projected_loading_visit_count",
  "projected_loading_visit_quantity_tons",
  "projected_group_quantity_tons",
  "other_group_quantity_tons",
  "distance_delta_km",
  "duration_delta_minutes",
  "projected_work_minutes",
  "effective_max_work_minutes",
  "overtime_minutes",
  "overtime_authorized",
  "route_source",
  "route_contract_verified",
]);

function formatMetric(key, value) {
  if (value == null || value === "") {
    return "Non disponible";
  }
  if (typeof value === "boolean") {
    return value ? "Oui" : "Non";
  }
  if (typeof value !== "number") {
    if (key === "route_source") {
      return value === "unchanged_same_sites"
        ? "Sites existants"
        : value === "cache"
          ? "Cache local"
          : "Non disponible";
    }
    return String(value);
  }
  if (key.includes("minutes")) {
    return `${formatNumber(value, 0)} min`;
  }
  if (key.includes("km")) {
    return `${formatNumber(value)} km`;
  }
  if (key.includes("tons")) {
    return `${formatNumber(value)} t`;
  }
  if (key.includes("loading_visit_count") || key === "loading_visit_delta") {
    return formatNumber(value, 0);
  }
  return formatNumber(value, 2);
}

function PreviewMetrics({ metrics }) {
  const normalizedMetrics = metrics && typeof metrics === "object" ? metrics : {};
  const routeContractVerified = normalizedMetrics.route_contract_verified === true;
  const entries = Object.entries(normalizedMetrics)
    .filter(([key, value]) => (
      PUBLIC_METRIC_KEYS.has(key)
      && key !== "route_contract_verified"
      && value !== undefined
    ));
  if (!entries.length && !routeContractVerified) {
    return <span className="text-slate-500">Mesures locales non disponibles</span>;
  }
  return (
    <dl className="mt-2 grid gap-1.5 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-baseline justify-between gap-3 rounded-[8px] bg-white/85 px-2.5 py-1.5">
          <dt className="text-[0.68rem] font-semibold text-slate-500">{METRIC_LABELS[key]}</dt>
          <dd className="m-0 text-[0.72rem] font-black text-slate-800">{formatMetric(key, value)}</dd>
        </div>
      ))}
      {routeContractVerified ? (
        <div className="flex items-baseline justify-between gap-3 rounded-[8px] bg-emerald-50 px-2.5 py-1.5">
          <dt className="text-[0.68rem] font-semibold text-emerald-800">Route HERE conservée</dt>
          <dd className="m-0 text-[0.72rem] font-black text-emerald-900">Oui</dd>
        </div>
      ) : null}
    </dl>
  );
}

function CandidateBlock({
  active = false,
  analysisActive = false,
  block,
  disabled = false,
  onSelectTarget,
  onToggle,
  truckType = null,
  selection = null,
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const detailsId = React.useId();
  const blockId = normalizeText(block?.block_id);
  const requiredTruckType = normalizeTruckType(truckType) || resolveRequiredTruckType(block);
  const truckTypeLabels = PULL_FORWARD_TRUCK_TYPE_LABELS[requiredTruckType] || PULL_FORWARD_TRUCK_TYPE_LABELS[TRUCK_TYPE_CITERNE];
  const progressState = blockProgressState(block);
  const showProgressBadge = analysisActive && ["pending", "running", "error"].includes(progressState);
  const businessStatus = businessStatusDescriptor(block);
  const hasBusinessBadge = Boolean(businessStatus.label);
  const isReady = isReadyBusinessStatus(block)
    && (["completed", "compatible"].includes(progressState) || !normalizeText(block?.progress_state));
  const isSelected = selection !== null;
  const allTargets = targetOptionsForBlock(block, requiredTruckType);
  const readyTargets = allTargets
    .filter(isReadyTarget);
  const manualSplitTarget = normalizedBusinessStatus(block) === "manual_split_required"
    ? allTargets.find((target) => (
      normalizedBusinessStatus(target) === "manual_split_required"
      || normalizedReasonCode(target) === "client_site_split_required"
    ))
    : null;
  const selectedTargetKey = selection
    ? `${normalizeText(selection.targetTruckId)}::${normalizeText(selection.targetTripId)}`
    : "";
  const requiresTargetChoice = isSelected && allTargets.length > 1;
  const orderIds = (Array.isArray(block?.order_ids) ? block.order_ids : [])
    .map(normalizeText)
    .filter(Boolean);
  const clientName = normalizeText(block?.client_name) || "Client sans nom";
  const loadingClientName = normalizeText(block?.loading_client_name) || "Chargement non résolu";
  const product = normalizeText(block?.grain_type || block?.product) || "Produit non précisé";
  const reasonLabel = normalizeText(block?.reason_label || block?.reason_detail);
  const needsDispatchUpdate = requiresCurrentDispatchUpdate(reasonLabel);
  const displayReasonLabel = dispatcherReasonLabel(reasonLabel);
  const orderCount = Math.max(0, Number(block?.order_count) || 0);
  const compactOrderSummary = orderIds.length === 1 ? orderIds[0] : "";
  const targetCount = allTargets.length;
  const readyTargetCount = readyTargets.length;
  const manualMetrics = manualSplitTarget?.metrics && typeof manualSplitTarget.metrics === "object"
    ? manualSplitTarget.metrics
    : {};
  const manualCurrentQuantity = Number(
    manualSplitTarget?.current_group_quantity_tons ?? manualMetrics.current_group_quantity_tons,
  );
  const manualProjectedQuantity = Number(
    manualSplitTarget?.projected_group_quantity_tons ?? manualMetrics.projected_group_quantity_tons,
  );
  const manualCapacity = Number(manualSplitTarget?.capacity_tons ?? manualMetrics.capacity_tons);
  const manualAddedQuantity = manualProjectedQuantity - manualCurrentQuantity;
  const hasManualCalculation = [
    manualCurrentQuantity,
    manualAddedQuantity,
    manualProjectedQuantity,
    manualCapacity,
  ].every(Number.isFinite);
  const firstTargetLabel = targetCount === 1 ? targetLabel(allTargets[0], truckTypeLabels.title) : "";
  const hasDistinctTargetReason = allTargets.some((target) => {
    const targetReason = normalizeText(target?.reason_label);
    return targetReason && targetReason !== reasonLabel;
  });
  const hasExtraDetails = orderIds.length > 1 || allTargets.length > 1 || hasDistinctTargetReason;

  return (
    <article
      className={`rounded-[12px] border px-3 py-2 transition ${active ? "border-sky-400 bg-sky-50/75 ring-2 ring-sky-200" : isSelected ? "border-sky-300 bg-sky-50/45" : "border-slate-200 bg-white"}`}
      data-pull-forward-active={active ? "true" : undefined}
      data-pull-forward-block-status={normalizeText(block?.status).toLowerCase()}
      data-pull-forward-progress-state={progressState}
      id={`pull-forward-block-${blockId}`}
    >
      <div className="flex items-start gap-2.5">
        <input
          aria-label={`Sélectionner ${clientName || blockId}`}
          className="mt-0.5 h-4 w-4 shrink-0 accent-sky-700"
          type="checkbox"
          checked={isSelected}
          disabled={disabled || !isReady || readyTargets.length === 0}
          onChange={(event) => onToggle?.(blockId, event.target.checked)}
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="m-0 max-w-full truncate text-[0.79rem] font-black text-slate-950" title={clientName}>
                {clientName}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
              {showProgressBadge ? (
                <span
                  className={`rounded-full border px-2 py-0.5 text-[0.6rem] font-black ${progressStateClassName(progressState)}`}
                  aria-label={`État de l’analyse : ${progressStateLabel(progressState)}`}
                >
                  {progressStateLabel(progressState)}
                </span>
              ) : null}
              {hasBusinessBadge ? (
                <span
                  className={`rounded-full border px-2 py-0.5 text-[0.6rem] font-black ${needsDispatchUpdate ? "border-amber-200 bg-amber-50 text-amber-800" : statusClassName(block)}`}
                  title={needsDispatchUpdate ? "Le dispatch doit d’abord être actualisé" : statusLabel(block)}
                >
                  {dispatcherStatusLabel(block, reasonLabel)}
                </span>
              ) : null}
              {hasExtraDetails ? (
                <button
                  type="button"
                  aria-controls={detailsId}
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? "Masquer" : "Afficher"} les détails de ${clientName}`}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-900"
                  onClick={() => setIsExpanded((value) => !value)}
                >
                  <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                </button>
              ) : <span aria-hidden="true" className="h-6 w-6" />}
            </div>
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[0.65rem] font-semibold text-slate-600">
            <span>J+{Number(block?.day_offset) || "?"} · {formatDate(block?.source_date)}</span>
            <span className={`min-w-0 ${active ? "break-words" : "truncate"}`} title={compactOrderSummary || undefined}>
              {orderCount} commande{orderCount === 1 ? "" : "s"}{compactOrderSummary ? ` · ${compactOrderSummary}` : ""}
            </span>
            <span>{formatNumber(block?.quantity_tons)} t</span>
            <span className="max-w-44 truncate" title={product}>Produit · {product}</span>
            <span className="max-w-52 truncate" title={loadingClientName}>Chargement · {loadingClientName}</span>
            {targetCount ? (
              <span className="min-w-0 truncate text-sky-700" title={firstTargetLabel || undefined}>
                {manualSplitTarget && targetCount === 1
                  ? `${truckTypeLabels.singular} · ${targetLabel(manualSplitTarget, truckTypeLabels.title)} · ${normalizeText(manualSplitTarget?.target_trip_id)}`
                  : targetCount === 1 && readyTargetCount === 1 && firstTargetLabel
                  ? `${truckTypeLabels.singular} · ${firstTargetLabel}`
                  : `${readyTargetCount} cible${readyTargetCount === 1 ? "" : "s"} prête${readyTargetCount === 1 ? "" : "s"} sur ${targetCount}`}
              </span>
            ) : null}
          </div>
          {displayReasonLabel && !needsDispatchUpdate ? (
            <p className={`m-0 mt-1 text-[0.65rem] font-medium leading-4 text-slate-600 ${isExpanded ? "" : "truncate"}`} title={displayReasonLabel}>
              <strong className="text-slate-700">Pourquoi ?</strong> {displayReasonLabel}
            </p>
          ) : null}

          {manualSplitTarget ? (
            <div className="mt-2 rounded-[9px] border border-violet-200 bg-violet-50 px-2.5 py-2 text-[0.66rem] font-semibold leading-4 text-violet-950">
              <p className="m-0 font-black">
                Camion/trajet · {targetLabel(manualSplitTarget, truckTypeLabels.title)} · {normalizeText(manualSplitTarget?.target_trip_id) || "Trajet non précisé"}
              </p>
              {hasManualCalculation ? (
                <p className="m-0 mt-1">
                  {formatNumber(manualCurrentQuantity)} t existantes + {formatNumber(manualAddedQuantity)} t futures = {formatNumber(manualProjectedQuantity)} t sur {formatNumber(manualCapacity)} t
                </p>
              ) : null}
              <p className="m-0 mt-1 font-black">Réorganiser le Gantt puis relancer l’analyse.</p>
            </div>
          ) : null}

          {hasExtraDetails ? (
            <div id={detailsId} data-pull-forward-details hidden={!isExpanded}>
            {orderIds.length > 1 ? (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <span className="mr-2 text-[0.61rem] font-black uppercase tracking-[0.07em] text-slate-500">Commandes</span>
                <ul className="m-0 inline-flex list-none flex-wrap gap-1 p-0 align-middle" aria-label="Identifiants des commandes du bloc">
                  {orderIds.map((orderId) => (
                    <li key={orderId} className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[0.6rem] font-semibold text-slate-700">{orderId}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {allTargets.length > 1 || hasDistinctTargetReason ? (
              <div className="mt-2 grid gap-1 border-t border-slate-200 pt-2">
                <p className="m-0 text-[0.61rem] font-black uppercase tracking-[0.07em] text-slate-500">
                  {allTargets.length === 1
                    ? truckTypeLabels.singular
                    : `${readyTargetCount} cible${readyTargetCount === 1 ? "" : "s"} prête${readyTargetCount === 1 ? "" : "s"} sur ${allTargets.length}`}
                </p>
                {allTargets.map((target) => {
                  const capacityToFree = Number(target?.capacity_to_free_tons);
                  const targetReason = normalizeText(target?.reason_label || target?.reason_detail);
                  const hasDistinctReason = targetReason && targetReason !== reasonLabel;
                  const hasCapacityToFree = target?.status === "needs_capacity" && Number.isFinite(capacityToFree) && capacityToFree > 0;
                  const shouldShowCapacityToFree = hasCapacityToFree
                    || (businessStatusDescriptor(target).kind === "capacity" && Number.isFinite(capacityToFree) && capacityToFree > 0);
                  const targetStatus = businessStatusDescriptor(target);
                  return (
                    <div key={targetKey(target)} className="grid min-w-0 gap-x-2 rounded-[7px] bg-slate-50 px-2 py-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                      <span className="min-w-0 truncate text-[0.66rem] font-bold text-slate-800" title={targetLabel(target, truckTypeLabels.title)}>{targetLabel(target, truckTypeLabels.title)}</span>
                      {targetStatus.label ? (
                        <span className={`w-fit rounded-full border px-1.5 py-0.5 text-[0.57rem] font-black ${requiresCurrentDispatchUpdate(targetReason) ? "border-amber-200 bg-amber-50 text-amber-800" : statusClassName(target)}`}>
                          {dispatcherStatusLabel(target, targetReason)}
                        </span>
                      ) : null}
                      {targetReason && (hasDistinctReason || targetReason) ? (
                        <p className="m-0 text-[0.62rem] font-medium leading-4 text-slate-600 sm:col-span-2"><strong>Pourquoi ?</strong> {dispatcherReasonLabel(targetReason)}</p>
                      ) : null}
                      {shouldShowCapacityToFree ? (
                        <p className="m-0 text-[0.62rem] font-black text-amber-800 sm:col-span-2">{formatNumber(capacityToFree)} t à libérer</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            </div>
          ) : null}

          {isSelected ? (
            <div className="mt-2 border-t border-sky-200 pt-2">
              {allTargets.length === 1 ? (
                <p className="m-0 truncate text-[0.68rem] font-semibold text-slate-700" title={targetLabel(allTargets[0], truckTypeLabels.title)}>
                  {truckTypeLabels.singular} · cible : <strong>{targetLabel(allTargets[0], truckTypeLabels.title)}</strong>
                </p>
              ) : (
                <label className="grid items-center gap-1 text-[0.66rem] font-bold text-slate-700 sm:grid-cols-[auto_minmax(0,1fr)]">
                  <span>{truckTypeLabels.singular} · cible</span>
                  <select
                    aria-label={`Cible pour ${clientName || blockId}`}
                    className="h-8 min-w-0 rounded-[7px] border border-sky-200 bg-white px-2 text-[0.7rem] font-semibold text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                    disabled={disabled}
                    value={selectedTargetKey}
                    onChange={(event) => onSelectTarget?.(blockId, event.target.value)}
                  >
                    <option value="">Choisir une cible…</option>
                    {allTargets.map((target) => {
                      const isTargetReady = isReadyTarget(target);
                      return (
                        <option key={targetKey(target)} value={targetKey(target)} disabled={!isTargetReady}>
                          {targetLabel(target, truckTypeLabels.title)}{isTargetReady ? "" : ` — ${statusLabel(target)}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              {requiresTargetChoice && !selectedTargetKey ? (
                <p className="m-0 mt-1 text-[0.63rem] font-semibold text-amber-700">
                  Ce site existe dans plusieurs tournées; choisissez explicitement la cible.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

const PULL_FORWARD_GUIDE_STEPS = [
  {
    title: "Périmètre",
    description: "Analyse uniquement les commandes Citerne de J+1 et J+2 du dispatch et de la même période.",
  },
  {
    title: "Regrouper les commandes",
    description: "Même date, chargement, client/site et produit : les commandes sont traitées ensemble; un chargement non résolu reste séparé.",
  },
  {
    title: "Cibles",
    description: "Seuls les trajets du dispatch avec le même client et le même site sont proposés.",
  },
  {
    title: "Contraintes",
    description: "Journées, publication, protections, équipement, produit, capacité et temps de travail sont vérifiés.",
  },
  {
    title: "Dépendance J+2",
    description: "Un bloc J+2 dépend du bloc J+1 correspondant et garde ses cibles prêtes sur le même trajet.",
  },
  {
    title: "Diagnostic",
    description: "Les capacités insuffisantes sont écartées; une séparation manuelle reste visible comme conseil non sélectionnable.",
  },
  {
    title: "Prévisualisation",
    description: "Le replay vérifie les blocs entiers dans l’ordre J+1 puis J+2 avant l’application.",
  },
];

function diagnosticTruckTypeLabel(value) {
  return normalizeText(value).toLowerCase().includes(TRUCK_TYPE_CITERNE) ? "Citerne" : "—";
}

function diagnosticStateLabel(order) {
  return businessStatusDescriptor(order).label
    || normalizeText(order?.reason_label || order?.reason_code)
    || "État non documenté";
}

function diagnosticReasonText(value) {
  return normalizeText(value)
    .replace(/\s+\([^()]*\)(?=\s*[;,.])/gu, "")
    .replace(/\s{2,}/gu, " ")
    .trim();
}

function diagnosticReasonLabel(order) {
  const reasonCode = normalizeText(order?.reason_code || order?.reasonCode).toLowerCase();
  const stateLabel = normalizeText(diagnosticStateLabel(order));
  if (
    reasonCode === "citerne_exact_client_site_unavailable"
    || stateLabel === "Client/site absent du dispatch"
  ) {
    return "";
  }
  return diagnosticReasonText(order?.reason_label || order?.reason_code) || "—";
}

function diagnosticOrderDayLabel(order) {
  const dayOffset = Number(order?.day_offset);
  return Number.isFinite(dayOffset) ? `J+${dayOffset}` : "J+?";
}

function diagnosticTonnageLabel(order) {
  const quantity = Number(order?.quantity_tons);
  return Number.isFinite(quantity) ? `${formatNumber(quantity)} t` : "—";
}

function diagnosticProductLabel(order) {
  return normalizeText(order?.grain_type || order?.product) || "—";
}

function diagnosticOrderCount(order) {
  for (const key of ["block_order_count", "order_count"]) {
    const count = diagnosticCount(order, key);
    if (count !== null) {
      return count;
    }
  }
  if (Array.isArray(order?.order_ids) && order.order_ids.length) {
    return order.order_ids.length;
  }
  return normalizeText(order?.order_id) ? 1 : null;
}

function diagnosticClientLabel(order) {
  const clientName = normalizeText(order?.client_name) || "—";
  const count = diagnosticOrderCount(order);
  return count === null ? clientName : `(${formatNumber(count, 0)}) ${clientName}`;
}

function diagnosticLoadingLabel(order) {
  return normalizeText(order?.loading_client_name) || "—";
}

function diagnosticExcessLabel(order) {
  const targets = Array.isArray(order?.target_options) ? order.target_options : [];
  const capacityTargets = targets
    .filter((target) => normalizedBusinessStatus(target) === "needs_capacity")
    .map((target) => {
      const metrics = target?.metrics && typeof target.metrics === "object" ? target.metrics : {};
      return {
        excess: Number(target?.capacity_to_free_tons ?? metrics.capacity_to_free_tons),
        label: normalizeText(target?.truck_name) || normalizeText(target?.target_truck_id),
      };
    })
    .filter((target) => Number.isFinite(target.excess) && target.excess > 0)
    .sort((first, second) => first.excess - second.excess);
  if (!capacityTargets.length) {
    return "";
  }
  const bestTarget = capacityTargets[0];
  return `${formatNumber(bestTarget.excess)} t en trop${bestTarget.label ? ` · ${bestTarget.label}` : ""}`;
}

function diagnosticReasonWithExcessLabel(order) {
  return [diagnosticExcessLabel(order), diagnosticReasonLabel(order)]
    .filter(Boolean)
    .join(" · ") || "—";
}

function PullForwardGuide() {
  return (
    <div id="pull-forward-guide-panel" className="grid gap-3" data-pull-forward-guide="true">
      <div className="rounded-[14px] border border-sky-200 bg-sky-50 px-3.5 py-3 text-sky-950">
        <div className="flex items-start gap-2.5">
          <HelpCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
          <div>
            <h3 className="m-0 text-[0.9rem] font-black">Le regroupement Citerne en 7 étapes</h3>
            <p className="m-0 mt-1 text-[0.7rem] font-medium leading-5">
              Les blocs sont évalués ensemble, puis rejoués avant l’application.
            </p>
          </div>
        </div>
      </div>

      <ol className="m-0 grid list-none gap-2.5 p-0 sm:grid-cols-2 min-[1280px]:grid-cols-2">
        {PULL_FORWARD_GUIDE_STEPS.map((step, index) => (
          <li key={step.title} className="relative rounded-[12px] border border-slate-200 bg-white px-3 py-2.5 pl-11 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
            <span aria-hidden="true" className="absolute left-3 top-2.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-[0.68rem] font-black text-sky-800">
              {index + 1}
            </span>
            <h4 className="m-0 text-[0.75rem] font-black text-slate-900">{step.title}</h4>
            <p className="m-0 mt-1 text-[0.67rem] font-medium leading-4 text-slate-600">{step.description}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PullForwardDiagnostics({
  diagnostics,
  analysisActive = false,
  analysisComplete = true,
  analysisStatus = "",
  isOpen = false,
  panel = false,
}) {
  const [query, setQuery] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const model = normalizePullForwardDiagnostics(diagnostics, {
    analysisActive,
    analysisComplete,
    analysisStatus,
  });
  const filteredRows = filterPullForwardDiagnosticsOrders(model.rows, { filter, query });
  const diagnosticGroups = groupPullForwardDiagnosticsOrders(filteredRows);

  React.useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setFilter("all");
    }
  }, [isOpen]);

  return (
    <section className={panel ? "min-w-0" : "mt-4 border-t border-slate-200 pt-3"} aria-labelledby="pull-forward-diagnostics-title" data-pull-forward-diagnostics="true">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 id="pull-forward-diagnostics-title" className="m-0 text-[0.8rem] font-black text-slate-900">Diagnostic courant</h3>
          <p className="m-0 mt-0.5 text-[0.68rem] leading-4 text-slate-500">
            Commandes examinées par l’analyse; les lignes non affichées restent consultables ici sans être assimilées à une infaisabilité.
          </p>
        </div>
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-5">
        {[
          ["examined_order_count", "Commandes examinées"],
          ["shown_order_count", "Commandes affichées"],
          ["not_shown_order_count", "Commandes non affichées"],
          ["shown_block_count", "Blocs affichés"],
          ["hidden_block_count", "Blocs masqués"],
        ].map(([key, label]) => {
          const count = diagnosticCount(model.summary, key);
          return (
            <div key={key} className="rounded-[10px] border border-slate-200 bg-slate-50 px-2.5 py-2">
              <dt className="text-[0.61rem] font-semibold leading-3.5 text-slate-500">{label}</dt>
              <dd className="m-0 mt-0.5 text-[0.85rem] font-black text-slate-900">{count === null ? "—" : formatNumber(count, 0)}</dd>
            </div>
          );
        })}
      </dl>

      <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative flex min-w-0 flex-1 items-center">
          <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-slate-400" />
          <span className="sr-only">Rechercher une commande, un client ou un produit</span>
          <input
            aria-label="Rechercher commande, client ou produit"
            className="h-8 w-full rounded-[8px] border border-slate-200 bg-white pl-8 pr-2.5 text-[0.7rem] font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
            placeholder="Rechercher commande, client ou produit"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="flex shrink-0 flex-wrap items-center gap-1" role="group" aria-label="Filtrer les diagnostics">
          {PULL_FORWARD_DIAGNOSTIC_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={filter === option.key}
              className={`h-8 rounded-full border px-2.5 text-[0.65rem] font-black transition ${filter === option.key ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-sky-800"}`}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {model.rows.length ? (
        <div className="mt-2 w-full overflow-x-auto rounded-[12px] border border-slate-200" tabIndex="0" aria-label={`Tableau des diagnostics par ${model.usesBlocks ? "bloc" : "commande"}`}>
          <table className="min-w-[44rem] w-full table-fixed border-collapse text-left text-[0.64rem]">
            <colgroup>
              <col className="w-[7%]" />
              <col className="w-[8%]" />
              <col className="w-[6%]" />
              <col className="w-[7%]" />
              <col className="w-[11%]" />
              <col className="w-[16%]" />
              <col className="w-[17%]" />
              <col className="w-[28%]" />
            </colgroup>
            <thead className="bg-slate-50 text-[0.6rem] uppercase tracking-[0.04em] text-slate-500">
              <tr>
                <th className="whitespace-nowrap px-2.5 py-2 font-black">J+n</th>
                <th className="whitespace-nowrap px-2.5 py-2 font-black">Type</th>
                <th className="w-14 whitespace-nowrap px-2 py-2 font-black">Qt</th>
                <th className="min-w-16 px-1.5 py-2 font-black">Produit</th>
                <th className="min-w-24 px-1.5 py-2 font-black">Chargement</th>
                <th className="min-w-24 px-1.5 py-2 font-black">Client</th>
                <th className="min-w-32 px-1.5 py-2 font-black">État</th>
                <th className="min-w-48 px-1.5 py-2 font-black">Raison</th>
              </tr>
            </thead>
            {filteredRows.length ? diagnosticGroups.map((group) => (
              <tbody key={group.key} className="divide-y divide-slate-100 bg-white" data-pull-forward-diagnostic-group={group.key}>
                <tr className={group.key === "shown" ? "bg-sky-50/90 text-sky-950" : group.key === "hidden" ? "bg-amber-50/80 text-amber-950" : "bg-slate-100 text-slate-700"}>
                  <th colSpan="8" scope="rowgroup" className="px-2.5 py-1.5 text-[0.62rem] font-black uppercase tracking-[0.06em]">
                    {group.label} · {group.orders.length}
                  </th>
                </tr>
                {group.orders.map((order, index) => (
                  <tr
                    key={`${group.key}-${normalizeText(order?.block_id || order?.order_id) || "row"}-${index}`}
                    className={group.key === "shown" ? "align-top bg-emerald-50/80 font-semibold text-emerald-950 hover:bg-emerald-100" : "align-top text-slate-700 hover:bg-sky-50/35"}
                    data-pull-forward-diagnostic-retained={group.key === "shown" ? "true" : undefined}
                  >
                    <td className={`whitespace-nowrap px-2.5 py-2 font-black ${group.key === "shown" ? "border-l-4 border-emerald-500 pl-1.5 text-emerald-950" : "text-slate-900"}`}>
                      {group.key === "shown" ? <CheckCircle2 aria-label="Ligne retenue" className="mr-1 inline-block h-3.5 w-3.5 text-emerald-600" /> : null}
                      {diagnosticOrderDayLabel(order)}
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-2 font-semibold">{diagnosticTruckTypeLabel(order?.required_truck_type)}</td>
                    <td className="w-14 whitespace-nowrap px-2 py-2 font-semibold">{diagnosticTonnageLabel(order)}</td>
                    <td className="max-w-20 break-words px-1.5 py-2">{diagnosticProductLabel(order)}</td>
                    <td className="max-w-28 break-words px-1.5 py-2" title={diagnosticLoadingLabel(order)}>{diagnosticLoadingLabel(order)}</td>
                    <td className="max-w-28 break-words px-1.5 py-2 font-semibold">{diagnosticClientLabel(order)}</td>
                    <td className="max-w-44 break-words px-1.5 py-2 font-semibold">{diagnosticStateLabel(order)}</td>
                    <td className="max-w-64 break-words px-1.5 py-2" title={diagnosticReasonWithExcessLabel(order)}>{diagnosticReasonWithExcessLabel(order)}</td>
                  </tr>
                ))}
              </tbody>
            )) : (
              <tbody className="bg-white">
                <tr>
                  <td colSpan="8" className="px-3 py-4 text-center font-semibold text-slate-500">Aucune ligne ne correspond à cette recherche ou à ce filtre.</td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      ) : (
        <p className="m-0 mt-2 rounded-[10px] border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-[0.68rem] font-semibold text-slate-600">
          {model.state === "running" ? "Les lignes de diagnostic seront disponibles lorsque l’analyse aura progressé." : "Aucune ligne de diagnostic détaillée n’est disponible pour le moment."}
        </p>
      )}
    </section>
  );
}

export default function PullForwardDialog({ controller }) {
  const closeButtonRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const guideButtonRef = React.useRef(null);
  const restoreFocusRef = React.useRef(null);
  const [isGuideOpen, setIsGuideOpen] = React.useState(false);
  const isOpen = controller?.isOpen === true;
  const isApplying = controller?.isApplying === true;
  const returnToProposals = () => {
    setIsGuideOpen(false);
    globalThis.setTimeout(() => focusBillingDialogElement(guideButtonRef.current), 0);
  };
  const handleDialogClose = () => {
    if (isGuideOpen) {
      returnToProposals();
      return;
    }
    controller.close?.();
  };
  const activeBlockIds = Array.isArray(controller?.activeBlockIds)
    ? controller.activeBlockIds
    : [];
  const activeBlockKey = activeBlockIds.join("|");
  const previousActiveBlockKeyRef = React.useRef("");

  React.useEffect(() => {
    if (!isOpen) {
      setIsGuideOpen(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    restoreFocusRef.current = typeof document !== "undefined" ? document.activeElement : null;
    focusBillingDialogElement(closeButtonRef.current || dialogRef.current);
    return () => {
      focusBillingDialogElement(restoreFocusRef.current);
      restoreFocusRef.current = null;
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      previousActiveBlockKeyRef.current = "";
      return undefined;
    }
    if (!activeBlockKey || activeBlockKey === previousActiveBlockKeyRef.current) {
      return undefined;
    }
    previousActiveBlockKeyRef.current = activeBlockKey;
    const scrollTimer = globalThis.setTimeout(() => {
      const dialog = dialogRef.current;
      const focusedElement = typeof document !== "undefined" ? document.activeElement : null;
      const focusIsOnUserContent = focusedElement
        && focusedElement !== closeButtonRef.current
        && focusedElement !== dialog
        && typeof focusedElement.matches === "function"
        && focusedElement.matches("input, button, select, textarea, [contenteditable='true']");
      if (!dialog || focusIsOnUserContent) {
        return;
      }
      const activeBlock = dialog.querySelector('[data-pull-forward-active="true"]');
      if (typeof activeBlock?.scrollIntoView === "function") {
        activeBlock.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 0);
    return () => globalThis.clearTimeout(scrollTimer);
  }, [activeBlockKey, isOpen]);

  if (!isOpen) {
    return null;
  }

  const blocks = Array.isArray(controller.blocks) ? controller.blocks : [];
  const selectionByBlock = controller.selectionByBlock || {};
  const previewItems = Array.isArray(controller.previewResponse?.items)
    ? controller.previewResponse.items
    : [];
  const candidateSections = buildCandidateSections(blocks);
  const visibleBlockCount = candidateSections.reduce((total, section) => total + section.blocks.length, 0);
  const periodLabel = controller.scope?.dispatchPeriod === "evening" ? "Soir" : "Jour";
  const needsCurrentDispatchUpdate = blocks.some((block) => requiresCurrentDispatchUpdate(block?.reason_label));
  const completedBlocks = Math.max(0, Number(controller.completedBlocks) || 0);
  const totalBlocks = Math.max(completedBlocks, Number(controller.totalBlocks) || blocks.length);
  const progressValue = totalBlocks > 0
    ? Math.min(100, Math.round((completedBlocks / totalBlocks) * 100))
    : controller.analysisComplete ? 100 : 0;
  const isAnalysisActive = ["running", "cancelling"].includes(controller.analysisStatus);
  const hasIncompleteAnalysis = controller.analysisComplete === false && !isAnalysisActive;
  const showAnalysisProgressPanel = !controller.analysisComplete || isAnalysisActive;

  return createPortal(
    <div className="fixed inset-0 z-modal isolate font-sans" data-pull-forward-dialog="true" role="presentation">
      <div className="absolute inset-0 overflow-y-auto bg-slate-950/45 px-3 py-5 backdrop-blur-xs sm:px-5">
        <div
          className="flex min-h-full items-center justify-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isApplying) {
              controller.close?.();
            }
          }}
        >
          <section
            aria-describedby="pull-forward-dialog-description"
            aria-labelledby="pull-forward-dialog-title"
            aria-modal="true"
            className={`w-full overflow-hidden rounded-[22px] border border-sky-200 bg-white text-slate-950 shadow-[0_28px_70px_rgba(15,23,42,0.28)] ${isGuideOpen ? "max-w-[min(108rem,calc(100vw-5rem))]" : "max-w-[min(58rem,calc(100vw-1.5rem))]"}`}
            onKeyDown={(event) => {
              if (event.key === "Escape" && isApplying) {
                return;
              }
              handleBillingDialogKeyDown(event, {
                activeElement: document.activeElement,
                dialog: dialogRef.current,
                onClose: controller.close,
              });
            }}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
          >
            <div className="h-1.5 bg-sky-700" />
            <div className="flex max-h-[calc(100dvh-3rem)] flex-col">
              <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 pb-3 pt-3.5 sm:px-5">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] border border-sky-200 bg-sky-50 text-sky-700">
                    <CalendarRange aria-hidden="true" className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-[0.64rem] font-black uppercase tracking-[0.16em] text-sky-700">Regroupement local</p>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                      <h2 id="pull-forward-dialog-title" className="m-0 mt-1 text-[1.2rem] font-semibold tracking-[-0.03em]">
                        Regrouper J+1/J+2
                      </h2>
                      <p id="pull-forward-dialog-description" className="m-0 text-[0.75rem] font-medium text-slate-600 sm:ml-auto">
                        {formatDate(controller.scope?.dispatchDate)} · {periodLabel} · Le dispatch courant conserve son ordre.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    ref={guideButtonRef}
                    type="button"
                    aria-label={isGuideOpen ? "Fermer le détail du regroupement" : "Ouvrir le détail du regroupement"}
                    aria-controls="pull-forward-guide-panel"
                    aria-expanded={isGuideOpen}
                    aria-pressed={isGuideOpen}
                    className={`inline-flex h-9 items-center justify-center rounded-full border px-3 text-[0.7rem] font-black transition ${isGuideOpen ? "border-sky-600 bg-sky-600 text-white" : "border-sky-200 bg-sky-50 text-sky-800 hover:border-sky-300 hover:bg-sky-100"} disabled:opacity-50`}
                    disabled={isApplying}
                    onClick={() => setIsGuideOpen((value) => !value)}
                    title={isGuideOpen ? "Fermer le détail" : "Cliquez ici pour le détail"}
                  >
                    <span className="whitespace-nowrap">Cliquez ici pour le détail</span>
                  </button>
                  <button
                    ref={closeButtonRef}
                    type="button"
                    aria-label="Fermer"
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950 disabled:opacity-50"
                    disabled={isApplying}
                    onClick={handleDialogClose}
                  >
                    <X aria-hidden="true" className="h-4 w-4" />
                  </button>
                </div>
              </header>

              <div className={`min-h-0 flex-1 px-4 py-3.5 sm:px-5 ${isGuideOpen ? "overflow-y-auto min-[1280px]:overflow-hidden" : "overflow-y-auto"}`}>
                {isGuideOpen ? (
                  <div className="grid min-h-0 min-w-0 gap-4 min-[1280px]:h-full min-[1280px]:grid-cols-[minmax(20rem,0.68fr)_minmax(0,1.55fr)]">
                    <div className="min-h-0 min-w-0 min-[1280px]:overflow-y-auto min-[1280px]:pr-1">
                      <PullForwardGuide />
                    </div>
                    <div className="min-h-0 min-w-0 min-[1280px]:overflow-y-auto min-[1280px]:pl-1">
                      <PullForwardDiagnostics
                        analysisActive={isAnalysisActive}
                        analysisComplete={controller.analysisComplete === true}
                        analysisStatus={controller.analysisStatus}
                        diagnostics={controller.candidateResponse?.diagnostics}
                        isOpen={isOpen}
                        panel
                      />
                    </div>
                  </div>
                ) : (
                <>
                {hasIncompleteAnalysis ? (
                  <div className="mb-3 flex items-start gap-2 rounded-[12px] border border-sky-200 bg-sky-50 px-3 py-2.5 text-[0.74rem] font-semibold leading-5 text-sky-900" role="status">
                    <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>La limite de temps a interrompu une partie de l’analyse. Les blocs non traités ne sont pas considérés comme infaisables.</span>
                  </div>
                ) : null}

                {controller.errorMessage ? (
                  <div className="mb-3 flex items-start justify-between gap-3 rounded-[12px] border border-red-200 bg-red-50 px-3 py-2.5 text-[0.74rem] font-semibold leading-5 text-red-800" role="alert">
                    <span className="flex min-w-0 items-start gap-2">
                      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{controller.errorMessage}</span>
                    </span>
                    {!isApplying ? (
                      <button type="button" className="shrink-0 underline underline-offset-2" onClick={controller.loadCandidates}>Recharger</button>
                    ) : null}
                  </div>
                ) : null}

                {showAnalysisProgressPanel ? (
                <div className="mb-3 rounded-[12px] border border-slate-200 bg-slate-50 px-3 py-2.5" aria-live="polite">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[0.7rem] font-bold text-slate-700">
                    <span>
                      {isAnalysisActive
                        ? `Analyse des blocs · ${completedBlocks}/${totalBlocks || "—"}`
                        : controller.analysisStatus === "failed"
                          ? "Analyse terminée avec erreur"
                          : controller.analysisStatus === "cancelled"
                            ? "Analyse annulée"
                            : controller.analysisComplete
                              ? "Analyse terminée"
                              : "Analyse en attente"}
                    </span>
                    {activeBlockIds.length ? (
                      <span className="text-sky-700">{activeBlockIds.length} bloc{activeBlockIds.length === 1 ? "" : "s"} actif{activeBlockIds.length === 1 ? "" : "s"}</span>
                    ) : null}
                  </div>
                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200"
                    role="progressbar"
                    aria-label="Progression de l’analyse"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressValue}
                  >
                    <div className="h-full rounded-full bg-sky-600 transition-[width] duration-300" style={{ width: `${progressValue}%` }} />
                  </div>
                  <span className="sr-only" aria-live="polite">
                    {activeBlockIds.length ? `Blocs en cours : ${activeBlockIds.join(", ")}.` : ""}
                  </span>
                </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="m-0 text-[0.8rem] font-black text-slate-900">Propositions J+1/J+2 · {visibleBlockCount}</p>
                    <p className="m-0 mt-0.5 max-w-[42rem] text-[0.68rem] leading-4 text-slate-500">
                      Les propositions sont classées par jour, client/site, chargement et produit.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-[0.7rem] font-black">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                      {controller.selectionTotals?.blockCount || 0} sélectionné(s) · max. {controller.limits?.maxBlocks || 9}
                    </span>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                      {controller.selectionTotals?.orderCount || 0} commande(s) · max. {controller.limits?.maxOrders || 121}
                    </span>
                  </div>
                </div>

                {needsCurrentDispatchUpdate ? (
                  <div className="mt-3 flex items-start gap-2.5 rounded-[12px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-950" role="status">
                    <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div className="min-w-0">
                      <p className="m-0 text-[0.72rem] font-black">Pourquoi les blocs sont-ils bloqués&nbsp;?</p>
                      <p className="m-0 mt-0.5 text-[0.68rem] font-medium leading-4">
                        Le dispatch du {formatDate(controller.scope?.dispatchDate)} a changé depuis sa dernière optimisation. Fermez cette fenêtre, cliquez sur « Mise à jour », puis relancez « Regrouper J+1/J+2 ». Si « Mise à jour » refuse l’action, relancez « Optimiser » pour recalculer la journée.
                      </p>
                    </div>
                  </div>
                ) : null}

                {controller.isLoadingCandidates && !blocks.length ? (
                  <div className="mt-3 flex min-h-40 items-center justify-center rounded-[14px] border border-dashed border-sky-200 bg-sky-50/40 text-[0.8rem] font-semibold text-sky-800" role="status">
                    <RefreshCw aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                    Analyse locale de J+1 et J+2…
                  </div>
                ) : visibleBlockCount > 0 ? (
                  <div className="mt-3 grid gap-4">
                    {candidateSections.map((section) => (
                      <section
                        key={section.type}
                        aria-labelledby={`pull-forward-${section.type}-title`}
                        className="rounded-[14px] border border-slate-200 bg-slate-50/55 p-2.5 sm:p-3"
                        data-pull-forward-section={section.type}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <h3 id={`pull-forward-${section.type}-title`} className="m-0 text-[0.79rem] font-black text-slate-900">
                              {section.title}
                            </h3>
                            <p className="m-0 mt-0.5 max-w-[42rem] text-[0.67rem] leading-4 text-slate-600">
                              {section.description}
                            </p>
                          </div>
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[0.62rem] font-black text-slate-600">
                            {section.blocks.length} bloc{section.blocks.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        {section.blocks.length ? (
                          <div className="mt-2 grid gap-1.5">
                            {section.blocks.map((block) => {
                              const blockId = normalizeText(block?.block_id);
                              const hasSelection = Object.prototype.hasOwnProperty.call(selectionByBlock, blockId);
                              return (
                                <CandidateBlock
                                  key={blockId}
                                  active={activeBlockIds.includes(blockId)}
                                  analysisActive={isAnalysisActive}
                                  block={block}
                                  disabled={controller.isMutating}
                                  onSelectTarget={controller.selectTarget}
                                  onToggle={controller.toggleBlock}
                                  selection={hasSelection ? selectionByBlock[blockId] : null}
                                  truckType={section.type}
                                />
                              );
                            })}
                          </div>
                        ) : (
                          <p className="m-0 mt-2 rounded-[10px] border border-dashed border-slate-300 bg-white px-3 py-2.5 text-[0.68rem] font-semibold text-slate-600">
                            {section.emptyLabel}
                          </p>
                        )}
                      </section>
                    ))}
                  </div>
                ) : controller.candidateResponse ? (
                  <div className="mt-3 rounded-[14px] border border-dashed border-slate-300 bg-slate-50 px-4 py-7 text-center">
                    <p className="m-0 text-[0.84rem] font-semibold text-slate-800">
                      {controller.analysisComplete === false
                        ? "Analyse interrompue avant conclusion"
                        : "Aucune proposition J+1/J+2 visible"}
                    </p>
                    <p className="m-0 mt-1 text-[0.72rem] text-slate-500">
                      {controller.analysisComplete === false
                        ? "Relancez l’analyse; aucun bloc manquant n’est considéré comme infaisable."
                        : "Les blocs en capacité insuffisante ou inadmissibles sont consultables dans « Écartées des propositions » du guide."}
                    </p>
                  </div>
                ) : null}

                {previewItems.length ? (
                  <section className="mt-4 border-t border-slate-200 pt-3" aria-labelledby="pull-forward-preview-title">
                    <div className="flex items-center justify-between gap-3">
                      <h3 id="pull-forward-preview-title" className="m-0 text-[0.8rem] font-black text-slate-900">Résultat de la prévisualisation</h3>
                      <span className={`rounded-full border px-2.5 py-1 text-[0.66rem] font-black ${controller.previewResponse?.can_apply ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                        {controller.previewResponse?.can_apply ? "Prêt à appliquer" : "À corriger"}
                      </span>
                    </div>
                    {controller.previewResponse?.analysis_complete === false ? (
                      <p className="m-0 mt-2 rounded-[10px] border border-sky-200 bg-sky-50 px-3 py-2 text-[0.7rem] font-semibold text-sky-900">
                        Prévisualisation partielle : les éléments non traités ne sont pas déclarés infaisables.
                      </p>
                    ) : null}
                    {controller.previewResponse?.summary ? (
                      <p className="m-0 mt-2 text-[0.7rem] font-semibold text-slate-600">
                        {Math.max(0, Number(controller.previewResponse.summary.block_count) || 0)} bloc(s) · {Math.max(0, Number(controller.previewResponse.summary.order_count) || 0)} commande(s) · {formatNumber(controller.previewResponse.summary.quantity_tons)} t
                      </p>
                    ) : null}
                    <div className="mt-2 grid gap-2">
                      {previewItems.map((item) => {
                        const previewStatus = businessStatusDescriptor(item);
                        return (
                          <article key={`${item?.block_id}-${item?.target_truck_id}-${item?.target_trip_id}`} className="rounded-[12px] border border-slate-200 bg-slate-50 px-3 py-2.5">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-[0.72rem] font-black text-slate-800">{normalizeText(item?.block_id) || "Bloc"}</span>
                              {previewStatus.label ? (
                                <span className={`rounded-full border px-2 py-0.5 text-[0.62rem] font-black ${previewStatus.className}`}>
                                  {previewStatus.label}
                                </span>
                              ) : null}
                            </div>
                            {normalizeText(item?.reason_label) ? (
                              <p className="m-0 mt-1.5 text-[0.69rem] font-medium leading-5 text-slate-600">{normalizeText(item.reason_label)}</p>
                            ) : null}
                            <PreviewMetrics metrics={item?.metrics} />
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ) : null}
                </>
                )}
              </div>

              <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-4 py-3 sm:px-5">
                {isGuideOpen ? (
                  <>
                    <p className="m-0 text-[0.68rem] font-medium text-slate-500">
                      Les propositions, le diagnostic et vos sélections sont conservés pendant la consultation du guide.
                    </p>
                    <button
                      type="button"
                      className="inline-flex h-9 items-center gap-2 rounded-full border border-sky-700 bg-sky-700 px-4 text-[0.74rem] font-bold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isApplying}
                      onClick={returnToProposals}
                    >
                      <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
                      Retour aux propositions
                    </button>
                  </>
                ) : (
                  <>
                    <p className="m-0 text-[0.68rem] font-medium text-slate-500">
                      Analyse sans solveur global ni requête HERE. « Prévisualiser » utilise un replay local rapide et ordonné.
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="h-9 rounded-full border border-slate-300 bg-white px-4 text-[0.74rem] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        disabled={isApplying}
                        onClick={controller.close}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-9 items-center gap-2 rounded-full border border-sky-300 bg-white px-4 text-[0.74rem] font-bold text-sky-800 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!controller.canPreview}
                        onClick={() => void controller.preview?.()}
                      >
                        {controller.isPreviewing ? <RefreshCw aria-hidden="true" className="h-3.5 w-3.5 animate-spin" /> : null}
                        {controller.isPreviewing ? "Prévisualisation…" : "Prévisualiser"}
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-9 items-center gap-2 rounded-full border border-sky-700 bg-sky-700 px-4 text-[0.74rem] font-bold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!controller.canApply}
                        onClick={() => void controller.apply?.()}
                      >
                        {controller.isApplying ? <RefreshCw aria-hidden="true" className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />}
                        {controller.isApplying ? "Application…" : "Appliquer"}
                      </button>
                    </div>
                  </>
                )}
              </footer>
            </div>
          </section>
        </div>
      </div>
    </div>,
    getReactPortalRoot(),
  );
}
